/**
 * Cost-per-head figures for the budget screen.
 *
 * Server owns the maths (fixed/per-head/step totals, averaging, and the
 * per-event marginal breakdown) per the repo's "no business logic in the
 * browser" rule — this is a thin fetch + snake_case-to-camelCase mapping
 * layer only. Ported from Kalligo's CostPerHeadService.ts; the backend
 * calculation this displays lives in ans-backend's
 * services/planning/budget.ts (getCostPerHeadSummary).
 */

import { apiGet, planningPath } from './PlanningApiClient';
import { CostPerHeadSummary, CostPerHeadEventBreakdown, CostBand } from '../types/ui/Budget';

const EMPTY_COST_BAND: CostBand = {
  floorHeadcount: 0,
  expectedHeadcount: 0,
  ceilingHeadcount: 0,
  floorCost: 0,
  expectedCost: 0,
  ceilingCost: 0,
  rsvpConfidencePct: 0,
};

interface ApiCostBand {
  floor_headcount?: number | null;
  expected_headcount?: number | null;
  ceiling_headcount?: number | null;
  floor_cost?: number | null;
  expected_cost?: number | null;
  ceiling_cost?: number | null;
  rsvp_confidence_pct?: number | null;
}

interface ApiCostPerHeadEventBreakdown {
  event_id?: number | string | null;
  event_title?: string | null;
  headcount_confirmed?: number | null;
  per_head_rate?: number | null;
  step_rate_total?: number | null;
  marginal_cost_per_head?: number | null;
  cost_band?: ApiCostBand | null;
}

interface ApiCostPerHeadSummary {
  fixed_total?: number | null;
  per_head_total?: number | null;
  step_total?: number | null;
  grand_total?: number | null;
  total_confirmed_guests?: number | null;
  average_cost_per_head?: number | null;
  excluded_item_ids?: (number | string)[] | null;
  events?: ApiCostPerHeadEventBreakdown[] | null;
  cost_band?: ApiCostBand | null;
}

function mapCostBand(band: ApiCostBand | null | undefined): CostBand {
  if (!band) return EMPTY_COST_BAND;
  return {
    floorHeadcount: band.floor_headcount ?? 0,
    expectedHeadcount: band.expected_headcount ?? 0,
    ceilingHeadcount: band.ceiling_headcount ?? 0,
    floorCost: band.floor_cost ?? 0,
    expectedCost: band.expected_cost ?? 0,
    ceilingCost: band.ceiling_cost ?? 0,
    rsvpConfidencePct: band.rsvp_confidence_pct ?? 0,
  };
}

const EMPTY_SUMMARY: CostPerHeadSummary = {
  fixedTotal: 0,
  perHeadTotal: 0,
  stepTotal: 0,
  grandTotal: 0,
  totalConfirmedGuests: 0,
  averageCostPerHead: 0,
  excludedItemIds: [],
  events: [],
  costBand: EMPTY_COST_BAND,
};

export async function loadCostPerHeadSummary(): Promise<CostPerHeadSummary> {
  try {
    const raw = await apiGet<ApiCostPerHeadSummary>(planningPath('/budget/cost-per-head'));
    if (!raw) return EMPTY_SUMMARY;

    const events: CostPerHeadEventBreakdown[] = (raw.events ?? [])
      .filter((e): e is NonNullable<typeof e> => e != null && e.event_id != null)
      .map((e) => ({
        eventId: String(e.event_id),
        eventTitle: e.event_title ?? 'Untitled event',
        headcountConfirmed: e.headcount_confirmed ?? 0,
        perHeadRate: e.per_head_rate ?? 0,
        stepRateTotal: e.step_rate_total ?? 0,
        marginalCostPerHead: e.marginal_cost_per_head ?? 0,
        costBand: mapCostBand(e.cost_band),
      }));

    return {
      fixedTotal: raw.fixed_total ?? 0,
      perHeadTotal: raw.per_head_total ?? 0,
      stepTotal: raw.step_total ?? 0,
      grandTotal: raw.grand_total ?? 0,
      totalConfirmedGuests: raw.total_confirmed_guests ?? 0,
      averageCostPerHead: raw.average_cost_per_head ?? 0,
      excludedItemIds: (raw.excluded_item_ids ?? []).map((id) => String(id)),
      events,
      costBand: mapCostBand(raw.cost_band),
    };
  } catch (error) {
    console.error('Failed to load cost-per-head summary:', error);
    return EMPTY_SUMMARY;
  }
}
